import React, { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';

import { makeCommaSeparatedString } from 'constants/helper';

import { getOrganizationFunctions } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

const validateObjectiveFields = (tag) => {
  if (!tag?.name || !tag?.objectiveLevel) {
    return false;
  }

  return true;
};

const getErrorText = (tag) => {
  const errorTexts = [];

  if (!tag?.name) {
    errorTexts.push('name');
  }
  if (!tag?.objectiveLevel) {
    errorTexts.push('level');
  }

  return errorTexts.length ? `Please add ${makeCommaSeparatedString(errorTexts)} to save your objective.` : '';
};
const defaultObjectiveState = {
  uuid: null,
  organizationId: null,
  name: '',
  displayName: '',
  parentTag: null,
  timeBoundDetails: {
    uuid: null,
    organizationId: null,
    startTS: null,
    endTS: null,
    timelineTag: null,
  },
  description: '',
  objectiveType: 'GOAL',
  objectiveLevel: null,
  objectiveId: null,
  canSave: false,
  objectiveTimeBoundId: null,
  errorText: getErrorText({}),
  /* Add owner and collaborators */
  otbPostCardId: null,
  ownersCollaborators: [],
  departmentId: null,
  departmentName: null,
};

const ObjectiveSidePanelContext = createContext(defaultObjectiveState);

const actions = {
  SET_NAME: 'SET_NAME',
  SET_TIMEBOUND_DETAILS: 'SET_TIMEBOUND_DETAILS',
  SET_DESCRIPTION: 'SET_DESCRIPTION',
  SET_OBJECTIVE_LEVEL: 'SET_OBJECTIVE_LEVEL',
  SET_PARENT_TAG: 'SET_PARENT_TAG',
  SET_OWNERS_AND_COLLABORATORS: 'SET_OWNERS_AND_COLLABORATORS',
  SET_DEPARTMENT_DETAILS: 'SET_DEPARTMENT_DETAILS',
};

const actionsReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case actions.SET_NAME:
      return {
        ...state,
        name: payload.name,
        displayName: payload.name,
        objectiveId: payload.objectiveId,
        canSave: validateObjectiveFields({
          ...state,
          name: payload.name,
          displayName: payload.name,
          objectiveId: payload.objectiveId,
        }),
        errorText: getErrorText({
          ...state,
          name: payload.name,
          displayName: payload.name,
          objectiveId: payload.objectiveId,
        }),
      };
    case actions.SET_TIMEBOUND_DETAILS:
      return {
        ...state,
        timeBoundDetails: payload.timeBoundDetails,
      };
    case actions.SET_DESCRIPTION:
      return {
        ...state,
        description: payload.description,
      };
    case actions.SET_OBJECTIVE_LEVEL:
      return {
        ...state,
        objectiveLevel: payload.objectiveLevel,
        canSave: validateObjectiveFields({
          ...state,
          objectiveLevel: payload.objectiveLevel,
        }),
        errorText: getErrorText({
          ...state,
          objectiveLevel: payload.objectiveLevel,
        }),
      };
    case actions.SET_PARENT_TAG:
      return {
        ...state,
        parentTag: payload.id ? { id: payload.id } : null,
        objectiveTimeBoundId: payload.objectiveTimeBoundId,
        otbPostCardId: payload?.otbPostCardId,
      };

    case actions.SET_OWNERS_AND_COLLABORATORS: {
      return {
        ...state,
        ownersCollaborators: payload.ownersCollaborators ?? [],
      };
    }
    case actions.SET_DEPARTMENT_DETAILS: {
      return {
        ...state,
        departmentId: payload.departmentId,
        departmentName: payload.departmentName,
      };
    }

    default:
      return state;
  }
};

export const ObjectiveSidePanelProvider = ({ children }) => {
  const [state, dispatch] = useReducer(actionsReducer, defaultObjectiveState);

  const [functions, setFunctions] = useState([]);
  const [loadingFunctions, setLoadingFunctions] = useState(true);

  const fetchFunctions = () => {
    getOrganizationFunctions()
      .then((res) => setFunctions(res))
      .catch((err) => {
        console.error('Error in fetching functions : ', err);
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setLoadingFunctions(false);
      });
  };

  useEffect(() => {
    fetchFunctions();
  }, []);

  const setName = useCallback((name, objectiveData) => {
    dispatch({
      type: actions.SET_NAME,
      payload: { name, ...(objectiveData ? { objectiveId: objectiveData.objectiveId } : {}) },
    });
  }, []);

  const setTimeboundDetails = useCallback((timeBoundDetails) => {
    dispatch({
      type: actions.SET_TIMEBOUND_DETAILS,
      payload: { timeBoundDetails },
    });
  }, []);

  const setDescription = useCallback((description) => {
    dispatch({
      type: actions.SET_DESCRIPTION,
      payload: { description },
    });
  }, []);

  const setObjectiveLevel = useCallback((objectiveLevel) => {
    dispatch({
      type: actions.SET_OBJECTIVE_LEVEL,
      payload: { objectiveLevel },
    });
  }, []);

  const setParentTag = useCallback((id, objectiveTimeBoundId, otbPostCardId) => {
    dispatch({
      type: actions.SET_PARENT_TAG,
      payload: { id, objectiveTimeBoundId, otbPostCardId },
    });
  }, []);

  const setOwnersCollaborators = useCallback((ownersCollaborators) => {
    dispatch({
      type: actions.SET_OWNERS_AND_COLLABORATORS,
      payload: { ownersCollaborators },
    });
  }, []);
  const setDepartmentDetails = useCallback((departmentId, departmentName) => {
    dispatch({
      type: actions.SET_DEPARTMENT_DETAILS,
      payload: { departmentId, departmentName },
    });
  }, []);

  return (
    <ObjectiveSidePanelContext.Provider
      value={{
        state,
        setName,
        setDescription,
        setTimeboundDetails,
        setObjectiveLevel,
        setParentTag,
        setOwnersCollaborators,
        functions,
        setDepartmentDetails,
      }}
    >
      {children}
    </ObjectiveSidePanelContext.Provider>
  );
};

export const useObjectiveSidepanel = () => useContext(ObjectiveSidePanelContext);
